import { useState } from 'react'

import { api } from '../api/client'
import type { Channel, Page, Team, TeamMember, User } from '../api/types'
import { Card, Empty, ErrorNote, Spinner, TableScroll } from '../components/ui'
import { useAction, useQuery } from '../hooks/useApi'
import { useAuth } from '../auth/AuthContext'

export function TeamsPage() {
  const { can, me } = useAuth()
  const teams = useQuery(() => api.get<Page<Team>>('/teams', { limit: 200 }), [])
  const channels = useQuery(() => api.get<Page<Channel>>('/channels', { limit: 200 }), [])
  // Лиду команды тоже нужно из кого выбирать, а права users.manage у него нет:
  // для него есть справочник имён без ролей и контактов.
  const users = useQuery(
    () =>
      can('users.manage')
        ? api.get<Page<User>>('/users', { limit: 500 })
        : api.get<User[]>('/users/directory').then((items) => ({
            items,
            total: items.length,
            limit: items.length,
            offset: 0,
          })),
    [],
  )
  const action = useAction()

  const [selected, setSelected] = useState<Team | null>(null)
  const [invite, setInvite] = useState({ email: '', full_name: '', password: '' })
  const [invited, setInvited] = useState<{ email: string; password: string } | null>(null)
  // null — форма закрыта, '' — создаём новую, id — редактируем существующую.
  const [editing, setEditing] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    parent: '',
    channels: [] as string[],
  })

  const members = useQuery(
    () =>
      selected
        ? api.get<TeamMember[]>(`/teams/${selected.id}/members`)
        : Promise.resolve<TeamMember[]>([]),
    [selected?.id],
  )

  // Правка самой команды — администратору; состав — ещё и её лиду.
  const writable = can('teams.write')
  const canManageMembers = selected?.can_manage_members ?? false

  const startCreate = () => {
    setEditing('')
    setForm({ name: '', slug: '', description: '', parent: '', channels: [] })
  }

  const startEdit = (team: Team) => {
    setEditing(team.id)
    setForm({
      name: team.name,
      slug: team.slug,
      description: team.description ?? '',
      parent: team.parent_id ?? '',
      channels: team.default_channel_ids ?? [],
    })
  }

  const save = async () => {
    const body = {
      name: form.name,
      slug: form.slug,
      description: form.description,
      parent_id: form.parent || null,
      default_channel_ids: form.channels,
    }
    const ok = await action.run(() =>
      editing ? api.put(`/teams/${editing}`, body) : api.post('/teams', body),
    )
    if (ok) {
      // Открытая справа карточка состава показывала бы старое название.
      if (editing && selected?.id === editing) setSelected({ ...selected, ...body } as Team)
      setEditing(null)
      teams.reload()
    }
  }

  const remove = async (team: Team) => {
    if (await action.run(() => api.del(`/teams/${team.id}`))) {
      if (selected?.id === team.id) setSelected(null)
      teams.reload()
    }
  }

  const toggleLead = async (member: TeamMember) => {
    if (!selected) return
    const ok = await action.run(() =>
      api.put(`/teams/${selected.id}/members/${member.user_id}`, {
        user_id: member.user_id,
        is_lead: !member.is_lead,
      }),
    )
    if (ok) members.reload()
  }

  const addMember = async (userId: string) => {
    if (!selected) return
    if (await action.run(() => api.post(`/teams/${selected.id}/members`, { user_id: userId })))
      members.reload()
  }

  const removeMember = async (userId: string) => {
    if (!selected) return
    if (await action.run(() => api.del(`/teams/${selected.id}/members/${userId}`))) members.reload()
  }

  const inviteMember = async () => {
    if (!selected) return
    const ok = await action.run(async () => {
      const result = await api.post<{ email: string; generated_password: string }>(
        `/teams/${selected.id}/members/invite`,
        {
          email: invite.email,
          full_name: invite.full_name,
          // Пустой пароль — платформа сгенерирует и покажет его один раз.
          password: invite.password || null,
        },
      )
      setInvited({ email: result.email, password: result.generated_password })
    })
    if (ok) {
      setInvite({ email: '', full_name: '', password: '' })
      members.reload()
      users.reload()
    }
  }

  const userLabel = (id: string) => {
    const user = users.data?.items.find((item) => item.id === id)
    return user ? user.full_name || user.email : id
  }

  return (
    <>
      <ErrorNote>{teams.error ?? action.error}</ErrorNote>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card
          title="Команды"
          actions={
            writable && (
              <button className="btn-primary" onClick={startCreate}>
                Новая команда
              </button>
            )
          }
        >
          {teams.loading && !teams.data ? (
            <Spinner />
          ) : (teams.data?.items.length ?? 0) === 0 ? (
            <Empty>Команд нет.</Empty>
          ) : (
            <TableScroll>
              <thead>
                <tr>
                  <th className="th">Название</th>
                  <th className="th">Slug</th>
                  <th className="th">Каналы по умолчанию</th>
                  <th className="th" />
                </tr>
              </thead>
              <tbody>
                {teams.data!.items.map((team) => (
                  <tr
                    key={team.id}
                    onClick={() => setSelected(team)}
                    className={`cursor-pointer hover:bg-ink-700/40 ${
                      selected?.id === team.id ? 'bg-ink-700/60' : ''
                    }`}
                  >
                    <td className="td">
                      {team.name}
                      {team.is_lead && (
                        <span className="ml-2 text-xs text-sky-400">вы лид</span>
                      )}
                    </td>
                    <td className="td font-mono text-xs text-slate-400">{team.slug}</td>
                    <td className="td text-xs text-slate-400">
                      {team.default_channel_ids
                        .map(
                          (id) =>
                            channels.data?.items.find((channel) => channel.id === id)?.name ?? '—',
                        )
                        .join(', ') || '—'}
                    </td>
                    <td className="td text-right">
                      {writable && (
                        <div className="flex gap-1 justify-end">
                          <button
                            className="btn-ghost text-xs"
                            // stopPropagation: клик по строке выбирает команду
                            // для панели состава, кнопка не должна этого делать.
                            onClick={(event) => {
                              event.stopPropagation()
                              startEdit(team)
                            }}
                          >
                            Изменить
                          </button>
                          <button
                            className="btn-danger text-xs"
                            onClick={(event) => {
                              event.stopPropagation()
                              remove(team)
                            }}
                          >
                            Удалить
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableScroll>
          )}
        </Card>

        <Card title={selected ? `Состав: ${selected.name}` : 'Состав команды'}>
          {!selected ? (
            <Empty>Выберите команду слева.</Empty>
          ) : (
            <div className="p-4 space-y-3">
              {(members.data ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">
                  В команде никого нет — эскалация «всю команду» никого не найдёт.
                </p>
              ) : (
                <ul className="space-y-1">
                  {members.data!.map((member) => (
                    <li key={member.id} className="flex items-center justify-between text-sm">
                      <span>
                        {userLabel(member.user_id)}
                        {member.is_lead && <span className="ml-2 text-xs text-sky-400">lead</span>}
                      </span>
                      {canManageMembers && (
                        <span className="flex gap-1">
                          <button className="btn-ghost text-xs" onClick={() => toggleLead(member)}>
                            {member.is_lead ? 'Снять lead' : 'Сделать lead'}
                          </button>
                          <button
                            className="btn-ghost text-xs"
                            onClick={() => removeMember(member.user_id)}
                          >
                            Убрать
                          </button>
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {canManageMembers && me?.local_accounts_enabled && (
                <div className="border-t border-ink-700 pt-3 space-y-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Нет учётной записи? Заведите
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input
                      className="input"
                      placeholder="email"
                      value={invite.email}
                      onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                    />
                    <input
                      className="input"
                      placeholder="имя"
                      value={invite.full_name}
                      onChange={(e) => setInvite({ ...invite, full_name: e.target.value })}
                    />
                    <input
                      className="input"
                      type="password"
                      placeholder="пароль (пусто — сгенерируем)"
                      value={invite.password}
                      onChange={(e) => setInvite({ ...invite, password: e.target.value })}
                    />
                  </div>
                  <button
                    className="btn-primary"
                    disabled={action.pending || !invite.email}
                    onClick={inviteMember}
                  >
                    Завести и добавить
                  </button>
                  {invited && (
                    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
                      <div>Учётная запись {invited.email} создана, роль — operator.</div>
                      {invited.password && (
                        <div className="mt-1">
                          Пароль:{' '}
                          <code className="font-mono text-xs break-all">{invited.password}</code>
                          <div className="text-xs text-slate-400 mt-1">
                            Показан один раз — передайте человеку и закройте это сообщение.
                          </div>
                        </div>
                      )}
                      <button className="btn-ghost text-xs mt-2" onClick={() => setInvited(null)}>
                        Скрыть
                      </button>
                    </div>
                  )}
                </div>
              )}

              {canManageMembers && users.data && (
                <div>
                  <label className="label">Добавить участника</label>
                  <select
                    className="input"
                    value=""
                    onChange={(event) => event.target.value && addMember(event.target.value)}
                  >
                    <option value="">— выберите пользователя —</option>
                    {users.data.items
                      .filter(
                        (user) => !(members.data ?? []).some((member) => member.user_id === user.id),
                      )
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name || user.email}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {editing !== null && (
        <Card
          title={editing ? 'Изменение команды' : 'Новая команда'}
          actions={
            <>
              <button className="btn-ghost" onClick={() => setEditing(null)}>
                Отмена
              </button>
              <button
                className="btn-primary"
                disabled={action.pending || !form.name || !form.slug}
                onClick={save}
              >
                {editing ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          <div className="p-4 grid lg:grid-cols-4 gap-4">
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Slug</label>
              <input
                className="input font-mono"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="sre"
              />
            </div>
            <div>
              <label className="label">Описание</label>
              <input
                className="input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Родительская команда</label>
              <select
                className="input"
                value={form.parent}
                onChange={(e) => setForm({ ...form, parent: e.target.value })}
              >
                <option value="">— нет —</option>
                {teams.data?.items
                  // Себя в родители не предлагаем: цикл всё равно отклонит сервер.
                  .filter((team) => team.id !== editing)
                  .map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="label">Каналы по умолчанию</label>
              <select
                className="input"
                multiple
                size={3}
                value={form.channels}
                onChange={(e) =>
                  setForm({
                    ...form,
                    channels: Array.from(e.target.selectedOptions, (option) => option.value),
                  })
                }
              >
                {channels.data?.items.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}
